/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import ErrorName from '../error/error-name';

/**
 * @import { FirmwareManager } from '../firmware'
 * @import { Logger } from './logger'
 * @import { LoggerInfo } from './logger-info'
 */

/**
 * @private
 * @typedef {Record<string, *>} AppManager TODO #555
 */

// TODO Rozšířit i o infomace přidané v DeviceStateful#getLoggerInfo
// - Bude možné až po migraci všech platforem na multiplatformní Adapter.
// - Přidá položky: deviceId, online, version.
/**
 * Správce informací pro logování.
 */
export default class LoggerManager {
  /**
   * Vytvoří instanci.
   * @param {AppManager} appManager Správce aplikace.
   * @param {FirmwareManager} firmwareManager Správce firmware.
   * @param {Logger} logger
   */
  constructor(appManager, firmwareManager, logger) {
    this.appManager = appManager;
    this.firmwareManager = firmwareManager;
    this.logger = logger;
  }

  /**
   * Zjistí informace pro logování.
   * @returns {Promise<LoggerInfo>} Informace.
   */
  getInfo() {
    return new Promise((resolve) =>
      resolve(
        [
          () => this.getFirmwareInfo(),
          () => this.getRuntimePlatform(),
          () => this.getUpdatePlatform(),
          () => this.getUpdateUrl()
        ].reduce(
          (accPromise, getter) =>
            accPromise.then((accObject) =>
              getter()
                .then((result) => Object.assign(accObject, result))
                .catch((error) => {
                  // Chyby vycházející z nedostupnosti informací se ignorují.
                  if (error.name !== ErrorName.NotSupportedError) {
                    this.logger.error(error);
                  }
                  return accObject;
                })
            ),
          Promise.resolve(/** @type {LoggerInfo} */ ({}))
        )
      )
    );
  }

  /**
   * Zjistí část informací o firmware:
   * - manufacturer,
   * - modelName
   * @private
   * @returns {Promise<Pick<LoggerInfo, 'modelName' | 'manufacturer'>>} Informace.
   */
  getFirmwareInfo() {
    return new Promise((resolve) =>
      resolve(
        this.firmwareManager.getInfo().then(({ modelName, manufacturer }) => ({
          modelName,
          manufacturer
        }))
      )
    );
  }

  /**
   * Zjistí identifikátor platformy, na které ja aplikace momentálně spuštěná.
   * @private
   * @returns {Promise<Pick<LoggerInfo, 'runtimePlatform'>>} Informace.
   */
  getRuntimePlatform() {
    return new Promise((resolve) =>
      resolve(
        this.firmwareManager
          .getRuntimePlatform()
          .then((runtimePlatform) => ({ runtimePlatform }))
      )
    );
  }

  /**
   * Zjistí část informací s platformou pro aktualizace.
   * @private
   * @returns {Promise<Pick<LoggerInfo, 'updatePlatform'>>} Informace.
   */
  getUpdatePlatform() {
    return new Promise((resolve) =>
      resolve(
        this.appManager
          .getUpdatePlatform()
          .then((updatePlatform) => ({ updatePlatform }))
      )
    );
  }

  /**
   * Zjistí část informací s aktualizační URL.
   * @private
   * @returns {Promise<Pick<LoggerInfo, 'updateUrl'>>} Informace.
   */
  getUpdateUrl() {
    return new Promise((resolve) =>
      resolve(
        this.appManager.getUpdateUrl().then((updateUrl) => ({ updateUrl }))
      )
    );
  }
}
